import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ArrowDownWideNarrow, Ban, Cake, CircleAlert, Copy, Crown, MailX, Plus, RotateCcw,
  Search, Timer, Trash2, Users,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { todayISO } from '../lib/dates'
import {
  EMPTY_STATS,
  aehnlicheGaeste,
  birthdayInfo, compareGuests, findDuplicates, gapsFor, guestLabel, guestStats,
  retentionCandidates, returnCandidates, summariseGuestFile, upcomingBirthdays, vipStatus,
  type DuplicatePair, type GuestFileSummary, type GuestProfile, type GuestSort,
  type GuestStats, type ReturnEntry,
} from '../lib/guests'
import { mayReceiveMarketing, type MarketingProfile } from '../lib/marketing'
import { Badge, Button, Card, EmptyState, Field, Input, Select, Sheet, cx } from '../ui/kit'
import GuestSheet, { GuestContactLine } from './GuestSheet'
import GuestMergeDialog from './GuestMergeDialog'
import { policyOf } from './guestUi'

/**
 * Wie weit voraus der Filter „Geburtstag" schaut.
 *
 * DREISSIG TAGE, also mehr als die vierzehn der Tagesübersicht. Die beiden Listen
 * haben verschiedene Aufgaben: die Tagesübersicht meldet, was ANSTEHT, und darf
 * deshalb nicht zu weit vorausgreifen. Dieser Filter ist die Arbeitsliste — wer sich
 * hinsetzt, um Geburtstagsmails zu schreiben, tut es nicht jeden Tag, sondern einmal
 * im Monat, und will dann alle sehen, die bis dahin dran sind.
 */
const BIRTHDAY_FILTER_DAYS = 30

/**
 * Die Gastkartei.
 *
 * Bisher war `guests` ein Adressbuch ohne Ansicht — man kam nur über das Suchfeld im
 * Buchungsblatt daran. Das reicht, um eine Rechnung zu adressieren, und nicht, um
 * einen Betrieb zu führen: wer zum vierten Mal kommt, wer Erdgeschoss braucht, wem
 * man zum Hochzeitstag eine Flasche hinstellt — das steht nirgends.
 *
 * DIE LISTE MUSS MIT HALBFERTIGEN DATENSÄTZEN GUT AUSSEHEN. Die Schnellerfassung im
 * Buchungsblatt legt einen Gast mit Namen an, jede Portalbuchung ebenso. Ein Balken
 * „30 % vollständig" wäre eine Beleidigung des Normalfalls; ein Hinweis „ohne E-Mail
 * keine Bestätigung möglich" ist eine Arbeitsanweisung. Deshalb der Filter
 * „Kontakt fehlt" statt einer Vollständigkeitsanzeige.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS AUS DEM AUVISO-CRM UND AUS BELLAVITA ÜBERNOMMEN IST — UND WAS NICHT.
 *
 * ÜBERNOMMEN:
 *   • Die KENNZAHLENLEISTE über der Liste (CRM `.cust-kpis`, Bellavita `KpiBox`).
 *     Eine Kartei ohne Kopfzeile ist ein Stapel Karteikarten. Mit der Zweitzeile aus
 *     Bellavita — Label, Wert, Hinweis — denn der Hinweis trägt den Bezugsrahmen
 *     („von 8", „€ 0,00 abgerechnet") und spart damit eine eigene Kachel.
 *   • Die SORTIERUNG als schlichtes Auswahlfeld (CRM `.cust-sort`). Auf dem Handy
 *     ein nativer Auswahldialog statt einer weiteren Reihe Chips.
 *   • Das KARTENRASTER, aber mit Bellavitas Grid-Formel
 *     `minmax(min(100%, 260px), 1fr)` statt einer Kette von Umbruchpunkten: die
 *     Karte wird nie breiter als ihr Behälter und fällt von allein auf eine Spalte.
 *   • Der ZUSTAND NEBEN DEM BETRAG, nicht IM Betrag (Bellavita). Die Zahl bleibt
 *     ruhig und tabellarisch, „offen" ist ein eigenes Abzeichen daneben.
 *
 * VERWORFEN:
 *   • Der AVATAR mit Initialen (beide Vorbilder haben ihn). Im CRM ist er ein
 *     Wiedererkennungsanker für Firmen, die man wöchentlich über Jahre öffnet. Hier
 *     stünde er auf dem Handy links neben einem Namen, der ohnehin danebensteht, und
 *     nähme 15 % der Kartenbreite — während rechts der Betrag drängt. Ein Gast wird
 *     ein-, zweimal im Jahr aufgeschlagen; da erkennt niemand eine Farbe wieder.
 *   • Der UMSCHALTER Karten/Tabelle (beide Vorbilder). Zwei Darstellungen zu pflegen
 *     lohnt sich bei dreitausend Kunden; bei ein paar hundert Gästen ist es eine
 *     Einstellung, die niemand findet, und eine Ansicht, die niemand prüft.
 *   • Der SPARKLINE-UMSATZVERLAUF der CRM-Karte. Er setzt Monatsumsätze über Jahre
 *     voraus. Ein Gast hat drei Datenpunkte in fünf Jahren — daraus eine Kurve zu
 *     zeichnen behauptet einen Verlauf, den es nicht gibt.
 *   • Der A–Z-STREIFEN und die SEITENBLÄTTERUNG aus Bellavita. Beide lösen ein
 *     Mengenproblem, das ein Haus mit sechs Einheiten nicht hat; das Suchfeld ist
 *     hier schneller als jeder Buchstabe.
 *   • VERTRAGS-, LIZENZ- UND ANLAGEN-ABZEICHEN der CRM-Karte. Deren Gegenstück wäre
 *     hier Herkunft, Sprache, Land, Vorlaufzeit, liebste Einheit — alles wahr,
 *     nichts davon eine Antwort auf die Frage, mit der man die Liste öffnet. Das
 *     steht im Gastblatt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Alle Zahlen sind gerechnet, keine gespeichert — siehe `src/lib/guests.ts`.
 * Texte inline, wie in `IcalCard` und `ComplianceCards`.
 */

type Filter =
  | 'all' | 'vip' | 'birthday' | 'return' | 'incomplete' | 'blocked'
  | 'duplicates' | 'retention'

export default function GuestsView() {
  const { lang, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const today = todayISO()

  /*
   * `?gast=<id>` öffnet das Blatt sofort — der kurze Weg aus einer Buchung heraus,
   * genau wie `?buchung=` in BookingsView. Der Parameter wird gleich wieder aus der
   * Adresse genommen, sonst öffnet ein Zurück im Browser dasselbe Blatt erneut.
   */
  const [params, setParams] = useSearchParams()
  const wanted = params.get('gast')

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [sort, setSort] = useState<GuestSort>('recent')
  const [active, setActive] = useState<GuestProfile | null>(null)
  const [pair, setPair] = useState<DuplicatePair | null>(null)
  /** Das Anlegeformular, solange es offen ist. Siehe `NeuerGast`. */
  const [neu, setNeu] = useState(false)

  const policy = policyOf(store.db.tenant)
  const retentionMonths =
    (store.db.tenant as { registration_retention_months?: number }).registration_retention_months
    ?? (store.db.tenant.country === 'DE' ? 12 : 84)

  // Anonymisierte Zeilen bleiben sichtbar — sie tragen die Buchungshistorie und der
  // Vermieter soll sehen, dass es sie gibt. Zusammengeführte Stummel nicht: die sind
  // nur noch der Anker einer alten Rechnung.
  const guests = useMemo(
    () => (store.db.guests as GuestProfile[]).filter((g) => !g.merged_into),
    [store.db.guests],
  )

  const statsById = useMemo(() => {
    const map = new Map<string, GuestStats>()
    for (const g of guests) {
      map.set(g.id, guestStats({
        guestId: g.id,
        bookings: store.db.bookings,
        invoices: store.db.invoices,
        payments: store.db.payments,
        today,
      }))
    }
    return map
  }, [guests, store.db.bookings, store.db.invoices, store.db.payments, today])

  // Eine Stelle, an der ein fehlender Gast zu leeren Kennzahlen wird — vorher stand
  // derselbe Aushilfsaufruf an drei Stellen, und drei Kopien sind drei Gelegenheiten,
  // eine davon zu vergessen.
  const statsOf = (id: string): GuestStats => statsById.get(id) ?? EMPTY_STATS

  useEffect(() => {
    if (!wanted) return
    const found = guests.find((g) => g.id === wanted)
    if (found) {
      setActive(found)
      setParams({}, { replace: true })
    }
  }, [wanted, guests])

  const duplicates = useMemo(() => findDuplicates(guests), [guests])
  const dueForDeletion = useMemo(
    () => retentionCandidates(guests, statsOf, policy, today),
    [guests, statsById, policy.retentionYears, today],
  )

  /*
   * Die Kopfzeile der Kartei. Rechnet nur mit `statsById`, das ohnehin schon steht —
   * keine zweite Runde durch Buchungen, Rechnungen und Zahlungen.
   */
  const summary = useMemo(
    () => summariseGuestFile(guests, statsOf, policy),
    [guests, statsById, policy.vipRevenueThreshold, policy.vipStayThreshold],
  )

  /*
   * Die beiden Marketing-Listen. Sie stehen als FILTER in derselben Liste und nicht
   * in einem eigenen Bereich: es sind Gäste, keine Kampagnen, und der Vermieter will
   * beim Durchgehen dieselben Angaben sehen wie sonst — wann er zuletzt da war, was
   * er gebracht hat, ob eine E-Mail-Adresse hinterlegt ist.
   */
  const birthdays = useMemo(
    () => upcomingBirthdays(guests, today, BIRTHDAY_FILTER_DAYS),
    [guests, today],
  )
  const birthdayIds = useMemo(
    () => new Set(birthdays.map((b) => b.guest.id)), [birthdays])

  const returning = useMemo(
    () => returnCandidates(guests, statsOf, today),
    [guests, statsById, today],
  )

  const isVip = (g: GuestProfile) => vipStatus(statsOf(g.id), policy, g.vip_override).vip

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return guests
      .filter((g) => {
        if (filter === 'vip') return isVip(g)
        if (filter === 'birthday') return birthdayIds.has(g.id)
        if (filter === 'incomplete') {
          return !g.anonymized_at && gapsFor(g).some((x) => x.weight === 'blocking')
        }
        if (filter === 'blocked') return g.blacklisted
        return true
      })
      .filter((g) => {
        if (!q) return true
        return [
          g.first_name, g.last_name, g.company, g.email, g.phone, g.city,
          g.vehicle_plate, ...(g.tags ?? []),
        ].filter(Boolean).join(' ').toLowerCase().includes(q)
      })
      .sort((a, b) => {
        // Beim Geburtstagsfilter zählt der nächste Termin und nicht der letzte
        // Aufenthalt — sonst steht der, der morgen dran ist, irgendwo in der Mitte.
        // Die gewählte Sortierung wird hier ÜBERSTIMMT und das Auswahlfeld deshalb
        // gar nicht erst angezeigt: ein Bedienelement, das sichtbar nichts tut, ist
        // schlimmer als eines, das fehlt.
        if (filter === 'birthday') {
          const da = birthdayInfo(a.birthdate, today)?.inDays ?? 999
          const db = birthdayInfo(b.birthdate, today)?.inDays ?? 999
          if (da !== db) return da - db
          return guestLabel(a).localeCompare(guestLabel(b))
        }
        return compareGuests(a, b, statsOf, sort)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests, query, filter, sort, statsById, birthdayIds, today])

  const counts: Record<Filter, number> = {
    all: guests.length,
    vip: guests.filter(isVip).length,
    birthday: birthdays.length,
    return: returning.length,
    incomplete: guests.filter(
      (g) => !g.anonymized_at && gapsFor(g).some((x) => x.weight === 'blocking')).length,
    blocked: guests.filter((g) => g.blacklisted).length,
    duplicates: duplicates.length,
    retention: dueForDeletion.length,
  }

  const FILTERS: { key: Filter; icon: typeof Users; de: string; en: string }[] = [
    { key: 'all', icon: Users, de: 'Alle', en: 'All' },
    { key: 'vip', icon: Crown, de: 'VIP', en: 'VIP' },
    { key: 'birthday', icon: Cake, de: 'Geburtstag', en: 'Birthday' },
    { key: 'return', icon: RotateCcw, de: 'Wiederkehr', en: 'Come back' },
    { key: 'incomplete', icon: CircleAlert, de: 'Kontakt fehlt', en: 'No contact' },
    { key: 'blocked', icon: Ban, de: 'Gesperrt', en: 'Blocked' },
    { key: 'duplicates', icon: Copy, de: 'Dubletten', en: 'Duplicates' },
    { key: 'retention', icon: Timer, de: 'Löschreif', en: 'Due for deletion' },
  ]

  return (
    <div className="p-4 pt-safe space-y-4 max-w-5xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">{de ? 'Gäste' : 'Guests'}</h1>
        {/* Bis zum 31.07.2026 entstand ein Gast ausschließlich aus einer Buchung
            heraus. Für einen Stammgast, der noch nichts gebucht hat — eine
            Anfrage am Telefon, eine Visitenkarte —, gab es keinen Weg in die
            Kartei. */}
        <Button size="sm" variant="primary" onClick={() => setNeu(true)}>
          <Plus size={15} />
          {de ? 'Neuer Gast' : 'New guest'}
        </Button>
        <div className="relative w-full max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input
            className="pl-9"
            placeholder={de ? 'Name, Mail, Kennzeichen …' : 'Name, mail, plate …'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <SummaryBar summary={summary} de={de} formatMoney={formatMoney} />

      {/* Chips wie in der Buchungsliste: auf dem Handy seitlich scrollend. */}
      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 scroll-x sm:overflow-visible">
        <div className="flex gap-2 sm:flex-wrap w-max sm:w-auto pb-1">
          {FILTERS.map(({ key, icon: Icon, de: dl, en: el }) => {
            const on = filter === key
            // Chips, die nur etwas melden, wenn es etwas zu melden gibt. Ein Chip
            // „Geburtstag 0" wäre eine Auskunft, nach der niemand gefragt hat.
            const onlyWhenAny = ['duplicates', 'retention', 'birthday', 'return']
            if (onlyWhenAny.includes(key) && counts[key] === 0) return null
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={cx(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm whitespace-nowrap',
                  on ? 'bg-accent text-accent-fg font-medium' : 'bg-surface-2 text-muted',
                )}
              >
                <Icon size={14} />
                {de ? dl : el}
                <span className="tabular-nums opacity-70">{counts[key]}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/*
        Die Sortierung steht UNTER den Chips und nicht neben der Überschrift: sie ist
        die zweite Entscheidung, nie die erste. Erst wird eingegrenzt, dann geordnet.
        Auf dem Handy ein Auswahlfeld und keine weitere Chipreihe — ein nativer
        Auswahldialog braucht einen Fingertipp und keine Bildschirmzeile.

        Nicht sichtbar bei den drei Filtern, die ihre eigene Ordnung mitbringen
        (Geburtstag nach Termin, Wiederkehr nach Jahrestag, Löschreif nach
        Belegdatum) — dort wäre das Feld ein Bedienelement, das sichtbar nichts tut.
      */}
      {(filter === 'all' || filter === 'vip' || filter === 'incomplete' || filter === 'blocked') && (
        <div className="flex items-center gap-2">
          <ArrowDownWideNarrow size={15} className="text-muted shrink-0" />
          <Select
            className="max-w-[15rem] py-2 text-sm"
            value={sort}
            onChange={(e) => setSort(e.target.value as GuestSort)}
            aria-label={de ? 'Sortierung' : 'Sort order'}
          >
            <option value="recent">{de ? 'Zuletzt da' : 'Last stay'}</option>
            <option value="value">{de ? 'Gebuchter Wert' : 'Booked value'}</option>
            <option value="stays">{de ? 'Häufigste Gäste' : 'Most stays'}</option>
            <option value="name">{de ? 'Name A–Z' : 'Name A–Z'}</option>
          </Select>
        </div>
      )}

      {filter === 'duplicates' ? (
        <DuplicateList
          pairs={duplicates} de={de}
          onPick={(p) => setPair(p)}
          onOpen={(g) => setActive(g)}
        />
      ) : filter === 'return' ? (
        <ReturnList
          items={returning} de={de} formatDate={formatDate}
          onOpen={(g) => setActive(g)}
        />
      ) : filter === 'retention' ? (
        <RetentionList
          items={dueForDeletion} de={de} years={policy.retentionYears}
          formatDate={formatDate} onOpen={(g) => setActive(g)}
        />
      ) : filter === 'birthday' ? (
        <GuestList
          rows={rows} statsById={statsById} policy={policy} filter={filter}
          today={today} de={de} formatDate={formatDate} formatMoney={formatMoney}
          onOpen={setActive}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Users size={28} />}
          title={de ? 'Keine Gäste' : 'No guests'}
          hint={de
            ? 'Gäste entstehen beim Anlegen einer Buchung — oder hier, über „Neuer Gast".'
            : 'Guests are created with a booking. Here they become a profile.'}
        />
      ) : (
        <GuestList
          rows={rows} statsById={statsById} policy={policy} filter={filter}
          today={today} de={de} formatDate={formatDate} formatMoney={formatMoney}
          onOpen={setActive}
        />
      )}

      {/*
        Die Meldescheinfrist steht sichtbar da, weil sie in Deutschland eine PFLICHT
        ist und nicht eine Möglichkeit: § 30 Abs. 4 BMG verlangt, Meldescheine ein
        Jahr nach Abreise aufzubewahren und danach binnen drei Monaten zu vernichten.
        Österreich verlangt mit § 10 MeldeG genau umgekehrt sieben Jahre Aufbewahrung.
        Ein Vermieter, der das nicht weiß, hält im einen Land zu lange und im anderen
        zu kurz — deshalb genügt es nicht, dass die Bereinigung nachts läuft; sie muss
        auch benannt sein. Die Frist folgt dem Land des Betriebs (Stammdaten).

        UNTER der Liste und nicht mehr darüber. Der Hinweis ist einmal zu lesen und
        danach dauerhaft wahr; auf dem Handy stand er als drei Zeilen Kleingedrucktes
        zwischen dem Vermieter und dem Gast, den er sucht. Weggelassen wird er nicht —
        eine Pflicht, die man nur findet, wenn man danach sucht, ist keine Auskunft.
      */}
      <p className="text-xs text-muted pt-2">
        {de
          ? `Meldescheine werden ${retentionMonths} Monate nach der Abreise automatisch vernichtet — ${
              store.db.tenant.country === 'DE'
                ? 'so verlangt es § 30 Abs. 4 BMG.'
                : 'nach Ablauf der siebenjährigen Mindestfrist des § 10 MeldeG.'
            } Belege bleiben davon unberührt.`
          : `Registration forms are destroyed automatically ${retentionMonths} months after departure. Invoices are unaffected.`}
      </p>

      <GuestSheet guest={active} open={active !== null} onClose={() => setActive(null)} />
      <NeuerGast
        open={neu}
        de={de}
        onClose={() => setNeu(false)}
        onCreated={(g) => { setNeu(false); setActive(g) }}
      />
      <GuestMergeDialog
        pair={pair} open={pair !== null}
        onClose={() => setPair(null)} onMerged={() => setPair(null)}
      />
    </div>
  )
}

/* ─────────────────────────────────────────────── Kennzahlenleiste */

/**
 * Die vier Zahlen über der Kartei.
 *
 * SIE FOLGT DEM FILTER NICHT. Das ist der eine bewusste Unterschied zum CRM, wo der
 * Kennzahlenstreifen den Zeitraum und die Auswahl mitrechnet. Hier tragen die
 * Filterchips ihre eigenen Zahlen — würde die Leiste sich mitbewegen, stünde dieselbe
 * Zahl zweimal auf demselben Bildschirm und man müsste jedes Mal prüfen, ob sie
 * dasselbe meinen. Die Leiste beschreibt die Kartei, die Chips beschreiben die
 * Auswahl.
 *
 * MOBIL: zwei mal zwei. Vier Kacheln nebeneinander wären auf 375 Pixeln vier
 * abgeschnittene Beträge, und ein abgeschnittener Betrag ist schlimmer als keiner.
 * Die Zweitzeile trägt den Bezugsrahmen — ohne sie ist „3" nur eine Ziffer.
 */
function SummaryBar({
  summary, de, formatMoney,
}: { summary: GuestFileSummary; de: boolean; formatMoney: (v: number) => string }) {
  if (summary.guests === 0) return null

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <Kpi
        label={de ? 'Gäste' : 'Guests'}
        value={String(summary.guests)}
        hint={de
          ? `${summary.withStay} schon da gewesen`
          : `${summary.withStay} have stayed`}
      />
      {/*
        Der Bezugsrahmen ist „mit Aufenthalt" und nicht „alle Gäste". Ein Prozentsatz
        auf die ganze Kartei würde jede Anfrage mitzählen, die nie ein Aufenthalt
        geworden ist, und die Quote damit systematisch kleinrechnen.
      */}
      <Kpi
        label={de ? 'Wiederkehrer' : 'Returning'}
        value={String(summary.returning)}
        hint={summary.withStay > 0
          ? (de ? `von ${summary.withStay} mit Aufenthalt` : `of ${summary.withStay} who stayed`)
          : (de ? 'ab dem 2. Aufenthalt' : 'from the 2nd stay')}
      />
      {/*
        GEBUCHT UND ABGERECHNET NEBENEINANDER — die beiden Kacheln, wegen denen diese
        Leiste überhaupt so aussieht. „Gebucht" ist die Zahl, die ein Vermieter meint,
        wenn er fragt, was seine Gäste bringen. „Abgerechnet" ist die Zahl aus den
        Rechnungen; wer keine schreibt, sieht dort dauerhaft null. Getrennt und
        beschriftet ist das eine Auskunft über die eigene Belegführung — zu einer Zahl
        verschmolzen wäre es eine Erfindung.

        Der Hinweis unter „Abgerechnet" wechselt: offene Beträge sind die einzige
        Sorte Geld, wegen der man aufsteht.
      */}
      <Kpi
        label={de ? 'Gebucht' : 'Booked'}
        value={formatMoney(summary.booked)}
        hint={de ? 'Wert der Aufenthalte' : 'value of stays'}
      />
      <Kpi
        label={de ? 'Abgerechnet' : 'Invoiced'}
        value={formatMoney(summary.invoiced)}
        hint={summary.open > 0
          ? (de ? `${formatMoney(summary.open)} offen` : `${formatMoney(summary.open)} outstanding`)
          : (de ? 'aus Rechnungen' : 'from invoices')}
        alert={summary.open > 0}
      />
    </div>
  )
}

function Kpi({
  label, value, hint, alert,
}: { label: string; value: string; hint: string; alert?: boolean }) {
  return (
    // Auf dem Handy schmaler und kleiner: die Leiste ist eine Kopfzeile und darf
    // nicht die halbe erste Bildschirmhöhe fressen, bevor der erste Gast kommt.
    <div className="rounded-xl border border-border bg-surface px-2.5 py-1.5 sm:px-3 sm:py-2 min-w-0">
      <div className="text-[11px] text-muted truncate">{label}</div>
      <div className="text-base sm:text-lg font-semibold tabular-nums truncate leading-tight">{value}</div>
      <div className={cx('text-[11px] truncate', alert ? 'text-warn' : 'text-muted')}>{hint}</div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────── Die Liste */

/**
 * Eine Karte je Gast in einem Raster.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS AUF DER KARTE STEHT — UND WARUM GENAU DAS.
 *
 * Die Kartei wird auf dem Handy geführt, während der Gast vor einem steht. Vier
 * Dinge braucht man in dieser Sekunde, und zwar in dieser Reihenfolge:
 *
 *   1. WER — Name, dazu Krone und Sperrvermerk als Zeichen, nicht als Wort.
 *   2. DER RHYTHMUS — „3× da · zuletzt 12.06.2026". Der eine Satz, wegen dem die
 *      Kartei existiert. Er ersetzt die drei Zahlenzeilen, die vorher rechts
 *      untereinander standen, weil man ihn LIEST statt ihn zu entziffern.
 *   3. DER WERT — was dieser Gast gebucht hat, rechts, tabellarisch, mit dem Wort
 *      „gebucht" darunter. Siehe die Umsatzentscheidung weiter unten.
 *   4. DER KONTAKT — E-Mail und Telefon. Das ist der Grund, warum jemand die Kartei
 *      am Anreisetag überhaupt aufmacht.
 *
 * Darunter nur AUSNAHMEN: offener Betrag, fehlende E-Mail, No-Shows, ein
 * Geburtstag in den nächsten dreissig Tagen, die eigenen Schlagworte. Alles, was
 * bei den meisten Gästen leer wäre, erzeugt bei den meisten Gästen auch keine
 * Zeile — sonst gewöhnt man sich an, die Zeile zu überfliegen, in der eines Tages
 * „2× nicht erschienen" steht.
 *
 * WAS NICHT DRAUFSTEHT: Herkunft, Sprache, Land, Ø Dauer, Vorlaufzeit, liebste
 * Einheit, Nächte gesamt, Stornos, erster Aufenthalt. Alles wahr, alles im
 * Gastblatt, nichts davon eine Antwort auf die Frage, mit der man die Liste öffnet.
 * Eine Karte, die alles zeigt, zeigt nichts.
 *
 * DER UMSATZ IST DER GEBUCHTE WERT, NICHT DER ABGERECHNETE. Vorher stand hier
 * `stats.revenue` — die Summe der ausgestellten Rechnungen. Das ist die richtige
 * Zahl für die Buchhaltung und die falsche für die Kartei: ein Vermieter mit sechs
 * Einheiten schreibt oft gar keine Rechnung, und dann steht bei jedem einzelnen
 * Gast dauerhaft „€ 0,00". Die Kartei sieht kaputt aus, obwohl sie richtig rechnet.
 * Die Karte zeigt deshalb `stats.booked` und schreibt „gebucht" darunter — nicht
 * „Umsatz", denn das wäre eine Zahl, die aussieht wie Umsatz und keiner ist. Was
 * abgerechnet ist, steht in der Kennzahlenleiste und im Gastblatt; was offen ist,
 * steht als Abzeichen auf der Karte, weil man deswegen aufsteht.
 *
 * MOBIL ZUERST. Das Raster ist `minmax(min(100%, 260px), 1fr)` und kommt ohne
 * Umbruchpunkte aus: auf 375 Pixeln eine Spalte über die volle Breite — also eine
 * Zeile, wie vorher, nur besser beschriftet —, auf 1280 Pixeln drei. Halbleere
 * Kästen kann es nicht geben, weil die Karte auf dem Handy so breit ist wie die
 * Liste selbst.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function GuestList({
  rows, statsById, policy, filter, today, de, formatDate, formatMoney, onOpen,
}: {
  rows: GuestProfile[]
  statsById: Map<string, GuestStats>
  policy: ReturnType<typeof policyOf>
  filter: Filter
  today: string
  de: boolean
  formatDate: (iso: string) => string
  formatMoney: (v: number) => string
  onOpen: (g: GuestProfile) => void
}) {
  if (rows.length === 0) {
    return <EmptyState icon={<Users size={28} />} title={de ? 'Niemand' : 'Nobody'} />
  }
  return (
    <ul className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(min(100%,17rem),1fr))] items-start">
      {rows.map((g) => (
        <li key={g.id} className="min-w-0">
          <GuestCard
            guest={g}
            stats={statsById.get(g.id) ?? EMPTY_STATS}
            policy={policy}
            filter={filter}
            today={today}
            de={de}
            formatDate={formatDate}
            formatMoney={formatMoney}
            onOpen={onOpen}
          />
        </li>
      ))}
    </ul>
  )
}

function GuestCard({
  guest: g, stats: s, policy, filter, today, de, formatDate, formatMoney, onOpen,
}: {
  guest: GuestProfile
  stats: GuestStats
  policy: ReturnType<typeof policyOf>
  filter: Filter
  today: string
  de: boolean
  formatDate: (iso: string) => string
  formatMoney: (v: number) => string
  onOpen: (g: GuestProfile) => void
}) {
  const vip = vipStatus(s, policy, g.vip_override)
  const blockingGap = gapsFor(g).find((x) => x.weight === 'blocking')
  const birthday = birthdayInfo(g.birthdate, today)
  const hasContact = Boolean(g.email?.trim()) || Boolean(g.phone?.trim())

  return (
    <button
      onClick={() => onOpen(g)}
      className={cx(
        'w-full h-full text-left rounded-2xl border border-border bg-surface p-3',
        'flex flex-col gap-2 min-w-0',
        'hover:border-accent hover:bg-surface-2 transition-colors',
      )}
    >
      <div className="flex items-start gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            {vip.vip && <Crown size={13} className="text-warn shrink-0" />}
            {g.blacklisted && <Ban size={13} className="text-danger shrink-0" />}
            <span className="font-medium truncate">
              {guestLabel(g, de ? 'Ohne Namen' : 'No name')}
            </span>
          </div>
          <div className="text-xs text-muted">
            {filter === 'birthday'
              // Im Geburtstagsfilter arbeitet man die Liste nach Datum ab; der
              // Rhythmus wandert dann unter das Datum, damit die Zeile, nach der
              // sortiert wird, auch die erste ist.
              ? <BirthdayLine info={birthday} guest={g} de={de} />
              : rhythmLine(s, de, formatDate)}
          </div>
        </div>

        {/*
          Der Betrag bleibt ruhig und tabellarisch; sein Zustand steht als Abzeichen
          darunter und nicht als Farbe in der Zahl. Bei „noch nichts gebucht" ein
          Strich statt „€ 0,00" — eine Null ist eine Behauptung, ein Strich sagt,
          dass es nichts zu sagen gibt.
        */}
        <div className="text-right shrink-0">
          {s.booked > 0 ? (
            <>
              <div className="font-semibold tabular-nums leading-tight">
                {formatMoney(s.booked)}
              </div>
              <div className="text-[11px] text-muted">{de ? 'gebucht' : 'booked'}</div>
            </>
          ) : (
            <div className="text-muted tabular-nums">—</div>
          )}
        </div>
      </div>

      {/*
        Der Kontakt in eigener Zeile, weil er der Grund ist, warum man die Kartei am
        Anreisetag aufmacht. Fehlt er ganz, steht das da — und zwar warnend und an
        der Stelle, an der sonst die Adresse stünde. Eine leere Zeile sähe aus wie
        ein Darstellungsfehler und nicht wie eine fehlende Angabe; ein zusätzliches
        Abzeichen „keine E-Mail" daneben wäre dieselbe Auskunft zweimal, deshalb
        entfällt es in genau diesem Fall.
      */}
      <div className={cx('text-xs min-w-0', hasContact ? 'text-muted' : 'text-warn')}>
        {hasContact
          ? <GuestContactLine guest={g} />
          : <span>{de ? 'kein Kontakt hinterlegt' : 'no contact details'}</span>}
      </div>

      <CardFlags
        guest={g} stats={s} de={de} formatMoney={formatMoney}
        blocking={Boolean(blockingGap) && hasContact}
        birthday={filter === 'birthday' ? null : birthday}
      />
    </button>
  )
}

/**
 * „3× da · zuletzt 12.06.2026" — der Satz, wegen dem die Kartei existiert.
 *
 * Als SATZ und nicht als drei rechtsbündige Zahlen: „2× da / € 828,20 / 30.07.2026"
 * muss man entziffern, „2× da · zuletzt 30.07.2026" liest man. Und er trägt auch den
 * Fall, den es in jeder Kartei gibt und der vorher als „0× da" dastand: wer noch nie
 * hier war, hat entweder etwas gebucht oder nur angefragt, und das sind zwei sehr
 * verschiedene Menschen.
 */
function rhythmLine(
  s: GuestStats, de: boolean, formatDate: (iso: string) => string,
): string {
  /*
   * Bevorstehende Aufenthalte heissen hier „kommt" und NICHT „gebucht", obwohl sie
   * das sind: rechts auf derselben Karte steht „gebucht" unter dem Betrag, und ein
   * Wort, das zwei Zeilen weiter etwas anderes meint, ist schlimmer als ein
   * umständliches. „kommt wieder" sagt ausserdem, was der Vermieter wissen will.
   */
  const coming = s.upcoming === 0
    ? null
    : s.upcoming === 1
      ? (s.stays === 0
        ? (de ? 'kommt erstmals' : 'first visit booked')
        : (de ? 'kommt wieder' : 'coming again'))
      : (de ? `kommt ${s.upcoming}×` : `coming ${s.upcoming}×`)

  if (s.stays === 0) {
    if (coming) return de ? `noch nie da · ${coming}` : `never stayed · ${coming}`
    return de ? 'noch kein Aufenthalt' : 'no stay yet'
  }
  const bits = [de ? `${s.stays}× da` : `${s.stays} stay${s.stays > 1 ? 's' : ''}`]
  if (s.lastStay) bits.push(de ? `zuletzt ${formatDate(s.lastStay)}` : `last ${formatDate(s.lastStay)}`)
  if (coming) bits.push(coming)
  return bits.join(' · ')
}

/**
 * Die Abzeichen unter der Karte: ausschliesslich Ausnahmen, nach Dringlichkeit.
 *
 * Erst was Geld kostet (offen), dann was Arbeit blockiert (keine E-Mail), dann was
 * man über den Menschen wissen muss (nicht erschienen), dann was nett ist
 * (Geburtstag), zuletzt die eigenen Schlagworte. Storniert steht NICHT dabei — eine
 * Absage ist ärgerlich, aber sie ändert nichts daran, wie man den Gast behandelt;
 * sie steht im Gastblatt.
 */
function CardFlags({
  guest: g, stats: s, de, formatMoney, blocking, birthday,
}: {
  guest: GuestProfile
  stats: GuestStats
  de: boolean
  formatMoney: (v: number) => string
  blocking: boolean
  birthday: ReturnType<typeof birthdayInfo>
}) {
  const flags: ReactNode[] = []

  if (s.open > 0) {
    flags.push(
      <Badge key="open" tone="warn">
        {de ? `${formatMoney(s.open)} offen` : `${formatMoney(s.open)} outstanding`}
      </Badge>,
    )
  }
  if (blocking) {
    flags.push(
      <Badge key="mail" tone="warn">
        <MailX size={11} />{de ? 'keine E-Mail' : 'no email'}
      </Badge>,
    )
  }
  if (s.noShows > 0) {
    flags.push(
      <Badge key="noshow" tone="danger">
        {de ? `${s.noShows}× nicht erschienen` : `${s.noShows}× no-show`}
      </Badge>,
    )
  }
  // Nur im Monatsfenster. Ein Geburtstag in acht Monaten ist wahr und trotzdem
  // nichts, was heute auf einer Karte zu suchen hat.
  if (birthday && birthday.inDays <= BIRTHDAY_FILTER_DAYS) {
    flags.push(
      <Badge key="bday" tone={birthday.inDays <= 1 ? 'accent' : 'neutral'}>
        <Cake size={11} />
        {birthday.inDays === 0
          ? (de ? 'heute' : 'today')
          : birthday.inDays === 1
            ? (de ? 'morgen' : 'tomorrow')
            : (de ? `in ${birthday.inDays} T.` : `in ${birthday.inDays} d.`)}
      </Badge>,
    )
  }
  if (g.anonymized_at) {
    flags.push(
      <Badge key="anon" tone="neutral">{de ? 'anonymisiert' : 'anonymised'}</Badge>,
    )
  }
  // Die Schlagworte zuletzt und höchstens drei: sie sind die Notiz des Vermieters an
  // sich selbst und stehen deshalb hinter allem, was die Kartei ihm sagt.
  for (const tag of (g.tags ?? []).slice(0, 3)) {
    flags.push(
      <span key={`t-${tag}`} className="px-1.5 py-px rounded text-[10px] bg-surface-2 text-muted">
        {tag}
      </span>,
    )
  }

  if (flags.length === 0) return null
  return <div className="flex flex-wrap items-center gap-1">{flags}</div>
}

/* ─────────────────────────────────────────────────────── Geburtstag */

/**
 * Die Zeile unter dem Namen im Geburtstagsfilter: wann, wie alt — und ob eine Mail
 * überhaupt in Frage kommt. Der letzte Teil ist der wichtigste: eine Liste, die
 * zwanzig Namen zeigt, von denen drei angeschrieben werden dürfen, ist eine Liste,
 * die neunzehn Mal enttäuscht, wenn sie es nicht dazuschreibt.
 *
 * Als Zeile und nicht als rechte Spalte, seit der Wert dort steht: „in 4 Tagen · wird
 * 43" gehört unter den Namen, weil man in diesem Filter nach Terminen arbeitet und
 * nicht nach Beträgen.
 */
function BirthdayLine({
  info, guest, de,
}: { info: ReturnType<typeof birthdayInfo>; guest: GuestProfile; de: boolean }) {
  if (!info) return <span>{de ? 'kein Geburtsdatum' : 'no date of birth'}</span>
  const blocked = !mayReceiveMarketing(guest as MarketingProfile)
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      <span className={cx('font-medium', info.inDays <= 1 && 'text-accent')}>
        {info.inDays === 0
          ? (de ? 'heute' : 'today')
          : info.inDays === 1
            ? (de ? 'morgen' : 'tomorrow')
            : (de ? `in ${info.inDays} Tagen` : `in ${info.inDays} days`)}
      </span>
      <span>· {de ? `wird ${info.turning}` : `turns ${info.turning}`}</span>
      {blocked && (
        <span className="inline-flex items-center gap-1">
          · <MailX size={11} />{de ? 'keine Werbung' : 'no marketing'}
        </span>
      )}
    </span>
  )
}

/* ─────────────────────────────────────────────────── Wiederkehr */

/**
 * Gäste, deren Aufenthalt sich jährt — die Liste hinter der Wiederkehr-Ansprache.
 *
 * Eigene Ansicht statt einer Zeile in der Liste, weil hier eine Zahl zählt, die
 * sonst nirgends vorkommt: WANN sich der Aufenthalt jährt. Ohne sie wäre es nur eine
 * Auswahl von Gästen ohne erkennbaren Grund.
 */
function ReturnList({
  items, de, formatDate, onOpen,
}: {
  items: ReturnEntry[]
  de: boolean
  formatDate: (iso: string) => string
  onOpen: (g: GuestProfile) => void
}) {
  return (
    <div className="space-y-3">
      <Card className="p-3 space-y-1.5">
        <div className="font-medium flex items-center gap-2">
          <RotateCcw size={15} /> {de ? 'Wiederkehr' : 'Come back'}
        </div>
        <p className="text-sm text-muted">
          {de
            ? 'Diese Gäste waren um diese Jahreszeit schon einmal da und haben nichts Neues gebucht. Angezeigt werden sie drei bis dreizehn Wochen VOR dem Jahrestag — wer im Juli in den Bergen war, entscheidet im Frühjahr, wohin er heuer fährt, nicht im Juli.'
            : 'These guests stayed around this time of year and have nothing booked. They appear three to thirteen weeks before the anniversary, because that is when the decision is made.'}
        </p>
        <p className="text-sm text-muted">
          {de
            ? 'Eine Ansprache ist Werbung. Ohne Einwilligung des Gastes geht keine hinaus — das steht bei jedem Gast im Blatt.'
            : 'Such a message is advertising. Without the guest’s consent nothing goes out.'}
        </p>
      </Card>

      {items.length === 0 ? (
        <EmptyState
          icon={<RotateCcw size={28} />}
          title={de ? 'Gerade niemand' : 'Nobody right now'}
        />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border overflow-hidden bg-surface">
          {items.map(({ guest, lastArrival, anniversary, inDays, yearsAgo }) => {
            const blocked = !mayReceiveMarketing(guest as MarketingProfile)
            return (
              <li key={guest.id}>
                <button
                  onClick={() => onOpen(guest)}
                  className="w-full text-left p-3 flex items-center gap-3 hover:bg-surface-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{guestLabel(guest)}</div>
                    <div className="text-xs text-muted">
                      {de
                        ? `war am ${formatDate(lastArrival)} angereist — vor ${yearsAgo} Jahr${yearsAgo > 1 ? 'en' : ''}`
                        : `arrived ${formatDate(lastArrival)} — ${yearsAgo} year${yearsAgo > 1 ? 's' : ''} ago`}
                    </div>
                    <div className="text-xs text-muted">
                      {de
                        ? `Jahrestag ${formatDate(anniversary)}, in ${inDays} Tagen`
                        : `anniversary ${formatDate(anniversary)}, in ${inDays} days`}
                    </div>
                  </div>
                  {blocked
                    ? <Badge tone="neutral"><MailX size={12} />{de ? 'keine Werbung' : 'no marketing'}</Badge>
                    : <Badge tone="accent">{de ? 'darf Werbung' : 'may receive'}</Badge>}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────── Dubletten */

function DuplicateList({
  pairs, de, onPick, onOpen,
}: {
  pairs: DuplicatePair[]
  de: boolean
  onPick: (p: DuplicatePair) => void
  onOpen: (g: GuestProfile) => void
}) {
  if (pairs.length === 0) {
    return (
      <EmptyState
        icon={<Copy size={28} />}
        title={de ? 'Keine Dubletten gefunden' : 'No duplicates found'}
      />
    )
  }

  const reasonText = (r: DuplicatePair['reasons'][number]) => ({
    email: de ? 'gleiche E-Mail' : 'same email',
    phone: de ? 'gleiche Telefonnummer' : 'same phone',
    name: de ? 'gleicher Name' : 'same name',
    name_initial: de ? 'Name abgekürzt' : 'abbreviated name',
    place: de ? 'gleicher Ort' : 'same place',
  }[r])

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">
        {de
          ? 'Jede Portalbuchung legt einen neuen Gast an — „Maria Huber" mit zwei Adressen ist der Normalfall. Zusammenführen lässt sich nicht rückgängig machen, deshalb zeigt der nächste Schritt vorher, was passiert.'
          : 'Every portal booking creates a new guest. Merging cannot be undone, so the next step shows what will happen first.'}
      </p>
      {pairs.map((p) => (
        <Card key={`${p.a.id}-${p.b.id}`} className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Badge tone={p.score >= 90 ? 'danger' : p.score >= 75 ? 'warn' : 'neutral'}>
              {p.score >= 90
                ? (de ? 'sehr wahrscheinlich' : 'very likely')
                : (de ? 'möglich' : 'possible')}
            </Badge>
            <span className="text-xs text-muted">
              {p.reasons.map(reasonText).filter(Boolean).join(' · ')}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[p.a, p.b].map((g) => (
              <button
                key={g.id} onClick={() => onOpen(g)}
                className="text-left rounded-xl bg-surface-2 p-2.5 min-w-0"
              >
                <div className="font-medium truncate">{guestLabel(g)}</div>
                <div className="text-xs text-muted truncate">{g.email ?? '—'}</div>
                <div className="text-xs text-muted truncate">
                  {[g.phone, g.city].filter(Boolean).join(' · ') || '—'}
                </div>
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => onPick(p)}>
            <Copy size={14} />
            {de ? 'Zusammenführen prüfen' : 'Review merge'}
          </Button>
        </Card>
      ))}
    </div>
  )
}

/* ─────────────────────────────────────────────────────── Löschreife */

function RetentionList({
  items, de, years, formatDate, onOpen,
}: {
  items: { guest: GuestProfile; lastDocument: string; keepUntil: string }[]
  de: boolean
  years: number
  formatDate: (iso: string) => string
  onOpen: (g: GuestProfile) => void
}) {
  return (
    <div className="space-y-3">
      <Card className="p-3 space-y-1.5">
        <div className="font-medium flex items-center gap-2">
          <Timer size={15} /> {de ? 'Löschreif' : 'Due for deletion'}
        </div>
        <p className="text-sm text-muted">
          {de
            ? `Diese Gäste waren seit über ${years} Jahren nicht mehr da, haben keine offenen Vorgänge, und die Aufbewahrungsfrist ihres letzten Belegs ist abgelaufen. Art. 5 Abs. 1 lit. e DSGVO verlangt, personenbezogene Daten nicht länger zu speichern als nötig.`
            : `These guests have not stayed for over ${years} years, have nothing open, and the retention period of their last document has expired.`}
        </p>
        <p className="text-sm text-muted">
          {de
            ? 'Es wird nur vorgeschlagen. Automatisch gelöscht wird nichts — ein Job, der eigenmächtig Kundendaten entfernt, ist ein Fehler, den man nur einmal macht.'
            : 'These are suggestions only. Nothing is deleted automatically.'}
        </p>
      </Card>

      {items.length === 0 ? (
        <EmptyState
          icon={<Trash2 size={28} />}
          title={de ? 'Nichts zu löschen' : 'Nothing to delete'}
        />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border overflow-hidden bg-surface">
          {items.map(({ guest, lastDocument, keepUntil }) => (
            <li key={guest.id}>
              <button
                onClick={() => onOpen(guest)}
                className="w-full text-left p-3 flex items-center gap-3 hover:bg-surface-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{guestLabel(guest)}</div>
                  <div className="text-xs text-muted">
                    {de ? 'letzter Beleg ' : 'last document '}{formatDate(lastDocument)}
                    {' · '}
                    {de ? 'Frist abgelaufen am ' : 'retention ended '}{formatDate(keepUntil)}
                  </div>
                </div>
                <Badge tone="warn">{de ? 'prüfen' : 'review'}</Badge>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Einen Gast anlegen, ohne dass eine Buchung dahintersteht.
 *
 * Bis dahin gab es diesen Weg nicht: ein Gast entstand ausschließlich im
 * Buchungsblatt. Für den Anruf, der noch keine Buchung ist, für die
 * Visitenkarte vom Sommer, für den Stammgast, den man schon einträgt, bevor er
 * sich entscheidet — dafür fehlte er.
 *
 * Gefragt wird nur nach dem, was den Gast auffindbar macht. Alles Weitere
 * — Anschrift, Vorlieben, Ausweis, Marketing — steht in der Gastakte, und
 * genau dort landet man nach dem Anlegen. Ein Formular, das alle vierzig Felder
 * der Kartei abfragt, bevor man einen Namen notieren darf, wäre die
 * schlechtere Antwort auf dieselbe Lücke.
 */
function NeuerGast({
  open, de, onClose, onCreated,
}: {
  open: boolean
  de: boolean
  onClose: () => void
  onCreated: (g: GuestProfile) => void
}) {
  const store = useStore()
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) setForm({ first_name: '', last_name: '', email: '', phone: '' })
  }, [open])

  /*
   * Dieselbe Dublettenwarnung wie im Buchungsblatt, und aus demselben Grund:
   * die zweite „Familie Berger" fällt sonst erst auf, wenn beide eine Rechnung
   * haben. Die Warnung sperrt nichts — es gibt echte Namensgleichheit — sie
   * bietet den vorhandenen Gast an, mehr nicht.
   */
  const verdacht = open ? aehnlicheGaeste(form, store.db.guests as never) : []

  const anlegen = async () => {
    setBusy(true)
    const saved = await store.saveGuest({
      id: crypto.randomUUID(),
      tenant_id: store.db.tenant.id,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      language: store.db.tenant.locale,
      blacklisted: false,
    })
    setBusy(false)
    onCreated(saved as GuestProfile)
  }

  const brauchbar = Boolean(form.first_name.trim() || form.last_name.trim())

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={de ? 'Neuer Gast' : 'New guest'}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {de ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button
            variant="primary" className="flex-1"
            disabled={!brauchbar || busy}
            onClick={() => void anlegen()}
          >
            {de ? 'Anlegen' : 'Create'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label={de ? 'Vorname' : 'First name'}>
            <Input
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
          </Field>
          <Field label={de ? 'Nachname' : 'Last name'}>
            <Input
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </Field>
          <Field label={de ? 'E-Mail' : 'Email'}>
            <Input
              type="email" inputMode="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label={de ? 'Telefon' : 'Phone'}>
            <Input
              type="tel" inputMode="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
        </div>

        {verdacht.length > 0 && (
          <div className="bg-warn-soft text-warn rounded-xl p-3 space-y-1.5">
            <p className="text-xs font-medium">
              {de
                ? 'Könnte eine Dublette sein — diese Gäste gibt es schon:'
                : 'This could be a duplicate — these guests already exist:'}
            </p>
            {verdacht.map((v) => (
              <button
                key={v.guest.id}
                className="block w-full text-left text-xs underline underline-offset-2"
                /* Wie im Buchungsblatt: bei offener Tastatur kommt ein `click`
                   nicht an, weil das Ausblenden der Tastatur die Zeile unter dem
                   Finger wegschiebt. */
                onPointerDown={(e) => {
                  e.preventDefault()
                  onCreated(v.guest as GuestProfile)
                }}
              >
                {v.guest.first_name} {v.guest.last_name}
                {v.guest.email ? ` · ${v.guest.email}` : ''}
                {' — '}
                {de ? 'diesen öffnen' : 'open that one'}
              </button>
            ))}
          </div>
        )}

        <p className="text-xs text-muted">
          {de
            ? 'Anschrift, Vorlieben und alles Weitere stehen danach in der Gastakte.'
            : 'Address, preferences and everything else follow in the guest file.'}
        </p>
      </div>
    </Sheet>
  )
}
